import {
    Box,
    Stack,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import RecurringList from './RecurringList';
import ContractorRecurringModal from '../../Reservations/ReservationContractors/ContractorRecurringModal';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { useGetIndividualRecurringJobQuery } from '../../../../app/Slices/SevenDayApiSlice';

const RecurringJobs = () => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedJobId = searchParams.get('selectedJob');
    const theme = useTheme();

    const selectedJobIdNum = useMemo(() => {
        return selectedJobId && /^\d+$/.test(selectedJobId)
            ? Number(selectedJobId)
            : undefined;
    }, [selectedJobId]);

    const initialEnabled = (() => {
        const p = searchParams.get('enabled');
        return p === null ? true : p === 'true';
    })();

    const [tab, setTab] = useState<'enabled' | 'disabled'>(
        initialEnabled ? 'enabled' : 'disabled',
    );
    const [newRecurringJobOpen, setNewRecurringJobOpen] = useState(false);

    const { data: selectedJob } = useGetIndividualRecurringJobQuery(
        selectedJobIdNum as unknown as number,
        { skip: !selectedJobIdNum },
    );

    useEffect(() => {
        if (!selectedJobId || !selectedJob) return;

        const nextTab: 'enabled' | 'disabled' = selectedJob.enabled
            ? 'enabled'
            : 'disabled';

        if (tab !== nextTab) setTab(nextTab);

        const current = searchParams.get('enabled');
        const desired = selectedJob.enabled ? 'true' : 'false';
        if (current !== desired) {
            const next = new URLSearchParams(searchParams);
            next.set('enabled', desired);
            setSearchParams(next, { replace: true });
        }
    }, [selectedJobId, selectedJob, tab, searchParams, setSearchParams]);

    const handleTabChange = (_: any, value: 'enabled' | 'disabled') => {
        setTab(value);
        const next = new URLSearchParams(searchParams);
        next.set('enabled', value === 'enabled' ? 'true' : 'false');
        next.delete('selectedJob');
        setSearchParams(next, { replace: true });
    };

    return (
        <>
            <Stack sx={{ pt: '30px', gap: 4 }}>
                <div>
                    <Typography color='text.primary' sx={{ fontSize: '14px' }}>
                        Recurring jobs let you set up a task once and have the
                        system automatically generate jobs for future
                        reservations. Instead of manually creating a new job
                        each time, you define the recurring job with a pattern,
                        contractor and experience. From there, whenever a
                        guest’s reservation is approved, the system
                        automatically creates the corresponding job based on
                        your setup.
                    </Typography>

                    <Stack
                        direction='row'
                        justifyContent='flex-end'
                        width='100%'
                        spacing='5px'
                        sx={{ paddingTop: isMobile ? '10px' : '0px' }}
                    >
                        <CustomButton
                            type='button'
                            variant='contained'
                            color='primary'
                            fullWidth={isMobile}
                            onClick={() => setNewRecurringJobOpen(true)}
                        >
                            <EventRepeatIcon />
                            <span style={{ marginLeft: 5 }}>
                                New Recurring Job
                            </span>
                        </CustomButton>
                    </Stack>

                    <Stack spacing={1}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Tabs
                                value={tab}
                                onChange={handleTabChange}
                                variant='scrollable'
                                scrollButtons='auto'
                                sx={{ alignItems: 'flex-start' }}
                                TabIndicatorProps={{
                                    sx: {
                                        left: '8%',
                                        transform: 'translateX(-8%)',
                                        bottom: 0,
                                        borderRadius: '5px',
                                        backgroundColor:
                                            theme.palette.info.main,
                                        transition: 'all 150ms ease',
                                    },
                                }}
                            >
                                <Tab label='Enabled' value='enabled' />
                                <Tab label='Disabled' value='disabled' />
                            </Tabs>
                        </Box>

                        <RecurringList enabled={tab === 'enabled'} />
                    </Stack>
                </div>

                <ContractorRecurringModal
                    open={newRecurringJobOpen}
                    isEditing={false}
                    onClose={() => setNewRecurringJobOpen(false)}
                />
            </Stack>
        </>
    );
};

export default RecurringJobs;
